/**
 * Shared top-of-screen chrome.
 *
 * Layout (Instagram-style brand strip + per-screen title underneath):
 *
 *   ┌───────────────────────────────────────────────────────┐
 *   │ [bell]      [logo] Twelve Stones      [gear / back]   │ ← brand row
 *   │                                                       │
 *   │  Memories                              + Add photo    │ ← title row
 *   └───────────────────────────────────────────────────────┘
 *
 * - Bell (top-left) goes to /alerts. On /alerts itself the bell becomes a
 *   Back chevron.
 * - Gear (top-right) goes to /you. On /you itself the gear becomes a Back
 *   chevron.
 * - Back uses navigate(-1) with a /today fallback when there's no
 *   history (fresh PWA launch / direct deep-link).
 * - The title row's right slot is reserved for per-screen actions
 *   ("+ Add photo", "Manage", "Mark all read") — we never put Back
 *   there, so those actions stay in place.
 */
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUnreadCount } from '../lib/notifications';

export function ScreenHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  /** Optional secondary line under the title (date, plan day, etc.). */
  subtitle?: ReactNode;
  /** Optional right-side action for the title row (e.g. "+ Add photo"). */
  right?: ReactNode;
}) {
  return (
    <header
      className="px-5 pb-3 bg-paper border-b border-line"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
    >
      {/* Brand row — bell left, logo + wordmark centered, gear right. */}
      <div className="relative h-10 flex items-center justify-center">
        <HeaderLeft />
        {/* Decorative — the adjacent wordmark is the accessible name. */}
        <img src="/logo-mark.png" alt="" className="w-9 h-9 object-contain" />
        <span className="ml-2 font-serif text-xl text-ink leading-none tracking-tight">
          Twelve Stones
        </span>
        <HeaderRight />
      </div>

      {/* Title row — Fraunces 4xl, with optional right-side action. */}
      <div className="mt-4 flex items-end justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="font-serif text-4xl text-ink leading-none">{title}</h1>
          {subtitle && <div className="text-soft text-sm mt-2">{subtitle}</div>}
        </div>
        {right && <div className="shrink-0">{right}</div>}
      </div>
    </header>
  );
}

/** Top-left slot: Alerts bell, OR a Back chevron on full-screen sub-pages
 *  (Alerts, and the Sermon Notes editor) that the user drilled into. */
function HeaderLeft() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/alerts') || pathname.startsWith('/walk/sermon-notes')) {
    return <BackButton side="left" />;
  }
  return <BellLink />;
}

/** Top-right slot: Settings gear, OR a Back chevron when on /you. */
function HeaderRight() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/you')) {
    return <BackButton side="right" />;
  }
  return <GearLink />;
}

function BackButton({ side }: { side: 'left' | 'right' }) {
  const navigate = useNavigate();
  // Fresh PWA launches start at history length 1. Anything > 1 means
  // the user navigated into this page from somewhere — go there.
  // Otherwise drop them on Today as a safe default.
  function goBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate('/today');
  }
  return (
    <button
      onClick={goBack}
      aria-label="Back"
      className={`absolute ${side === 'left' ? 'left-0' : 'right-0'} text-ink flex items-center gap-1`}
    >
      {side === 'left' && <ChevronLeft />}
      <span className="text-sm font-medium">Back</span>
      {side === 'right' && <ChevronRight />}
    </button>
  );
}

function BellLink() {
  const unread = useUnreadCount();
  const count = unread.data?.count ?? 0;
  return (
    <Link
      to="/alerts"
      aria-label={count > 0 ? `${count} unread alerts` : 'Alerts'}
      className="absolute left-0 text-ink"
    >
      <span className="relative inline-block">
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
          <path
            d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4l2-2z"
            stroke="currentColor"
            strokeWidth={1.7}
            strokeLinejoin="round"
          />
          <path
            d="M10 20a2 2 0 0 0 4 0"
            stroke="currentColor"
            strokeWidth={1.7}
            strokeLinecap="round"
          />
        </svg>
        {count > 0 && (
          <span className="absolute -top-1 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-oxblood text-paper text-[10px] leading-[18px] text-center font-semibold">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </span>
    </Link>
  );
}

function GearLink() {
  return (
    <Link to="/you" aria-label="Settings" className="absolute right-0 text-ink">
      <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth={1.7} />
        <path
          d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06A2 2 0 1 1 4.37 16.96l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
          stroke="currentColor"
          strokeWidth={1.7}
          strokeLinejoin="round"
        />
      </svg>
    </Link>
  );
}

function ChevronLeft() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
      <path
        d="M15 6l-6 6 6 6"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
